import { useMutation, useQueryClient } from 'react-query';

import { getFriendlyErrorMessage, type IStandardErrorResponse } from 'src/api/common';
import { queryKeys } from 'src/services/api/constants';
import { useNotifications } from 'src/notifications';
import { apiClient } from 'src/services/api/client';
import { isStandardErrorResponse } from 'src/api/common';

type IUpdateUserRequest = {
    name?: string;
    email?: string;
    phone_number?: string | null;
}

const useUpdateLoggedInUser = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<void, IStandardErrorResponse, IUpdateUserRequest>(
        async (payload) => {

            if (payload.phone_number === null) {
                delete payload['phone_number'];
            }

            if (payload.email === null) {
                delete payload['email'];
            }
            if (payload.name === null) {
                delete payload['name'];
            }
            await apiClient('api/users/me', {
                method: 'patch',
                json: payload,
            });
        }, {
            onSuccess: () => {
                queryClient.invalidateQueries([queryKeys.loggedInUser]);
                queryClient.invalidateQueries(queryKeys.teamMembers);

                addNotification('User info updated', 'success');
            },
            onError: async (error) => {
                await queryClient.invalidateQueries(queryKeys.facebookIntegration);
                let errorMessage = error.msg || 'Failed to update user info';
                if (isStandardErrorResponse(error)) {
                    errorMessage = getFriendlyErrorMessage(error);
                }

                addNotification(errorMessage, 'error');
            }
        }
    );
};

type IUpdateUserProfilePhotoRequest = {
    profile_photo?: Blob | null;
}

const useUpdateLoggedInUserProfilePhoto = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<void, IStandardErrorResponse, IUpdateUserProfilePhotoRequest>(
        async (formData) => {
            if (!formData.profile_photo) {
                return;
            }
            const data = new FormData();
            if (formData.profile_photo) {
                data.append('profile_photo', formData.profile_photo);
            }

            await apiClient('api/users/me/photo', {
                method: 'post',
                body: data,
            });
        }, {
            onSuccess: () => {
                queryClient.invalidateQueries([queryKeys.loggedInUser]);
                queryClient.invalidateQueries(queryKeys.teamMembers);

                addNotification('Profile photo updated', 'success');
            },
            onError: async (error) => {
                await queryClient.invalidateQueries(queryKeys.facebookIntegration);
                let errorMessage = error.msg || 'Failed to update profile photo';
                if (isStandardErrorResponse(error)) {
                    errorMessage = getFriendlyErrorMessage(error);
                }

                addNotification(errorMessage, 'error');
            }
        }
    );
};

const useDeleteLoggedInUserProfilePhoto = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<void, IStandardErrorResponse>(
        async () => {
            await apiClient('api/users/me/photo', {
                method: 'delete',
            });
        }, {
            onSuccess: () => {
                queryClient.invalidateQueries([queryKeys.loggedInUser]);
                queryClient.invalidateQueries(queryKeys.teamMembers);

                addNotification('Profile photo deleted', 'success');
            },
            onError: async (error) => {
                await queryClient.invalidateQueries(queryKeys.facebookIntegration);
                let errorMessage = error.msg || 'Failed to delete profile photo';
                if (isStandardErrorResponse(error)) {
                    errorMessage = getFriendlyErrorMessage(error);
                }

                addNotification(errorMessage, 'error');
            }
        }
    );
};


export {
    useUpdateLoggedInUser,
    useUpdateLoggedInUserProfilePhoto,
    useDeleteLoggedInUserProfilePhoto
};
